import "@/styles/globals.css";

import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import { useState, useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import Header from "@/components/nav/header";
import { trackPageView } from "@/hooks/useAnalytics";
import Footer from "@/components/footer";
import NotFoundPage from "@/components/common/NotFoundPage";
import { getCategories, getLocations, getPostHogConfig } from "@/lib/api";
import type { LocationsResponse } from "@/lib/locations";
import { Sentry } from "@/lib/sentry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PostHogProvider } from "posthog-js/react";

export const Route = createRootRoute({
  beforeLoad: async () => {
    const [jobCategoriesWithTags, locations] = await Promise.all([
      getCategories(),
      getLocations() as Promise<LocationsResponse>,
    ]);
    return { jobCategoriesWithTags, locations };
  },
  loader: async () => {
    const posthogConfig = await getPostHogConfig();
    return { posthogConfig };
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      { title: "Data First Jobs" },
      {
        name: "description",
        content:
          "Browse remote jobs for data professionals by tech stack, skills, titles, and more. No more digging through countless filters for the right job.",
      },
      { property: "og:site_name", content: "Data First Jobs" },
      { property: "og:type", content: "website" },
      {
        property: "og:image",
        content: "https://datafirstjobs.com/og-default.png",
      },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
      {
        rel: "apple-touch-icon",
        href: "/apple-touch-icon.png",
        sizes: "180x180",
      },
    ],
    // Dev-only: /ui picker toolbar. Loaded during design explorations so
    // variants can be previewed in-browser; stripped from prod bundles.
    scripts: import.meta.env.DEV
      ? [{ src: "https://ui.sh/ui-picker.js" }]
      : undefined,
  }),
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});

function derivePage(pathname: string): string | null {
  if (pathname === "/") return "home";
  if (pathname === "/search") return "search";
  if (pathname === "/confirm") return "confirm";
  return null; // $slug pages handle their own tracking
}

function RootLayout() {
  const { posthogConfig } = Route.useLoaderData();
  const { jobCategoriesWithTags, locations } = Route.useRouteContext();
  const router = useRouter();

  const [queryClient] = useState(() => new QueryClient());

  useEffect(() => {
    // Track initial page view
    const page = derivePage(window.location.pathname);
    if (page) trackPageView(page);

    return router.subscribe("onResolved", ({ toLocation }) => {
      const p = derivePage(toLocation.pathname);
      if (p) trackPageView(p);
    });
  }, [router]);

  const content = (
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary
        onError={(error) => {
          if (
            error instanceof TypeError &&
            error.message.includes("dynamically imported module")
          ) {
            window.location.reload();
          }
        }}
        fallback={({ resetError }) => (
          <div className="flex min-h-dvh items-center justify-center p-8">
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
                Something went wrong
              </h1>
              <p className="mt-2 text-gray-600">
                Please try refreshing the page.
              </p>
              <button
                type="button"
                onClick={resetError}
                className="bg-polaris-orange mt-6 rounded-lg px-5 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
              >
                Try again
              </button>
            </div>
          </div>
        )}
      >
        <Header
          jobCategoriesWithTags={jobCategoriesWithTags}
          locations={locations}
        />
        <main>
          <Outlet />
        </main>
        <Footer
          jobCategoriesWithTags={jobCategoriesWithTags}
          locations={locations}
        />
      </Sentry.ErrorBoundary>
      <Scripts />
    </QueryClientProvider>
  );

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="from-warm-bg to-warm-bg-deep min-h-dvh bg-linear-to-b">
        {posthogConfig.key ? (
          <PostHogProvider
            apiKey={posthogConfig.key}
            options={{
              api_host: posthogConfig.host,
            }}
          >
            {content}
          </PostHogProvider>
        ) : (
          content
        )}
      </body>
    </html>
  );
}
